import {combineReducers, type ReducersMapObject} from 'redux';
import {Record} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';
import addCard, {type AddCardModalState} from 'web-app/react/modals-container/add-card-modal/reducer';
import type {TestChildActionsModalState} from 'web-app/react/test-child-actions/types';
import {type Action} from 'web-app/util/redux/action-types';

import addInvoiceReducer from './add-invoice/reducer';
import addInvoiceV2Reducer from './add-invoice-v2/reducer';
import selectSessionReducer from './select-session-modal/reducer';
import invoiceReducer from './invoice/reducer';
import linkContactReducer from './link-contact-modal/reducer';
import editBillPayerReducer from './edit-bill-payers/reducer';
import billPayerReducer from './bill-payer-modal/reducer';
import addPaymentReducer from './add-payment-modal/reducer';
import sessionReducer from './session-modal/reducer';
import productReducer from './product-modal/reducer';
import organizationPersonManageRolesReducer, {
    type OrganizationPersonManageRoleModal,
} from './organization-person-manage-roles/reducer';
import postObservationModalReducer from './post-observation-modal/reducer';
import draftManagerModalReducer from './draft-manager-modal/reducer';
import choosePublicFundingReducer, {type ChoosePublicFunding} from './choose-public-funding-modal/reducer';
import depositModalReducer from './deposit-modal/reducer';
import eventModalReducer from './event-modal/reducer';
import creditModalReducer, {type CreditModalState} from './credit-modal/reducer';
import accountItemMoveReducer from './account-item-move/reducer';
import surchargeModalReducer, {type SurchargeModalState} from './surcharge-modal/reducer';
import {type ApplyCreamModalState} from './apply-cream/reducer';
import recipientsReducer from './recipients-modal/reducer';
import likersReducer from './likers-modal/reducer';
import videoReducer from './video-modal/reducer';
import linkToFrameworkModalReducer from './link-to-framework-modal/reducer';
import observationReducer from './observation-modal/reducer';
import moveRulesReducer from './move-rule-modal/reducer';
import helpModalReducer from './help-modal/reducer';
import reviewObservationReducer from './review-observation-modal/reducer';
import advancedChildGroupMoveReducer from './advanced-child-group-move-modal/reducer';
import childDevelopmentDrilldownReducer, {
    type ChildDevelopmentDrilldownModal,
} from './child-development-drilldown-modal/reducer';
import peoplePickerModalReducer, {type PeoplePickerModalState} from './people-picker-modal/reducer';
import childProfileModalReducer, {type ChildProfileModalState} from './child-profile-modal/reducer';
import newChildModalReducer, {type NewChildModalState} from './new-child-modal/reducer';
import newEmployeeModalReducer, {type NewEmployeeModalState} from './new-employee-modal/reducer';
import newConversationReducer from './new-conversation-modal/reducer';
import holidayRSVPReducer from './holiday-rsvp-modal/reducer';
import newContactModalReducer, {type NewContactModalState} from './new-contact-modal/reducer';
import purchase from './purchase-modal/reducer';
import qualification, {type QualificationModalState} from './qualification-modal/reducer';
import paymentsVerification, {type PaymentsVerificationModalState} from './payments-verification-modal/reducer';
import payNow, {type PayNowModalState} from './pay-now-modal/reducer';
import attendanceSchedule, {type AttendanceScheduleModalState} from './attendance-schedule-modal/reducer';
import activityForm, {type ActivityFormModalState} from './activity-form-modal/reducer';
import activityLibrarySetting, {
    type ActivityLibrarySettingFormModalState,
} from './activity-library-setting-modal/reducer';
import inviteParents, {type InviteParentsModalState} from './invite-parents-modal/reducer';
import directDebitActivation, {type DirectDebitActivationState} from './direct-debit-activation/reducer';
import reportAccidentIncident, {type ReportAccidentIncidentModalState} from './manage-report-modal/reducer';
import abstractBudget, {type AbstractBudgetModalState} from './abstract-budget-modal/reducer';
import payoutDetails, {type PayoutDetailsModalState} from './payout-details-modal/reducer';
import permissionForm, {type PermissionRoleModalState} from './permission-role-form-modal/reducer';
import organizationPersonProfile, {
    type OrganizationPersonProfileModalState,
} from './organization-person-profile-modal/reducer';
import automaticPayments, {type AutomaticPaymentsModalState} from './automatic-payments-modal/reducer';
import vacationPlan, {type VacationPlanModalState} from './vacation-plan-modal/reducer';
import childPickup, {type ChildPickupModalState} from './child-pickup-modal/reducer';
import signIn, {type SignInModalState} from './signin-modal/reducer';
import newTag, {type NewTagModalState} from './new-tag-modal/reducer';
import switchRoomModal, {type SwitchRoomModalState} from './switch-room-modal/reducer';
import mealSettingsModal, {type MealSettingsModalState} from './meal-settings-modal/reducer';
import addChildrenBulk, {type AddChildrenBulkModalState} from './add-children-bulk-modal/reducer';
import newMedicationForm, {type NewMedicationFormModalState} from './new-medication-form-modal/reducer';
import newMedicationFormAnyChild, {
    type NewMedicationFormModalAnyChildState,
} from './new-medication-form-modal-any-child/reducer';
import medicationForm, {type MedicationFormModalState} from './medication-form-modal/reducer';
import medicationAdministration, {
    type MedicationAdministrationModalState,
} from './medication-administration-modal/reducer';
import activitiesModal, {type ActivitiesModalState} from './activities-modal/reducer';
import singleActivityModal, {type SingleActivityModalState} from './single-activity-modal/reducer';
import joinFamlyLiveMeetingModal, {type JoinFamlyLiveMeetingModalState} from './join-famly-live-meeting-modal/reducer';
import passwordModal, {type PromptPasswordModalState} from './prompt-password-modal/reducer';
import inquiryModalsReducer from './inquiry-modals/reducer';
import registerTemperatureModalReducer, {type RegisterTemperatureModalState} from './register-temperature/reducer';
import fileTaggingModal, {type FileTaggingModalState} from './file-tagging-modal/reducer';
import assessmentModal, {type AssessmentModalState} from './assessment-modal/reducer';
import assessmentHistoryModal, {type AssessmentHistoryModalState} from './assessment-history-modal/reducer';
import reportUGCModal, {type ReportUGCModalState} from './report-ugc-modal/reducer';
import addPaymentMethodModal, {type AddPaymentMethodModalState} from './add-payment-method-modal/reducer';
import {
    observationEditorReducer as observationEditor,
    type ObservationEditorState,
} from './observation-editor-modal/reducer';
import * as Actions from './actions';
import achInstantVerificationModal, {
    type AchInstantVerificationModalState,
} from './ach-instant-verification-modal/reducer';
import testChildActionsModal from './test-child-actions-modal/reducer';

class ModalContainerState extends Record<{
    readonly openModalsCount: number;
}>({
    openModalsCount: 0,
}) {}

const modalContainerReducer = makeReducer<ModalContainerState>(
    [
        match({action: () => ({type: Actions.openedType, payload: {}}), type: Actions.openedType}, state =>
            state.merge({
                openModalsCount: state.openModalsCount + 1,
            }),
        ),
        match({action: () => ({type: Actions.closedType, payload: {}}), type: Actions.closedType}, state =>
            state.merge({
                openModalsCount: state.openModalsCount - 1,
            }),
        ),
    ],
    new ModalContainerState(),
);

export interface ModalsState {
    modalsContainer: ModalContainerState;
    holidayRSVP: any;
    newConversation: any;
    advancedChildGroupMove: any;
    childDevelopmentDrilldown: ChildDevelopmentDrilldownModal;
    childProfileModal: ChildProfileModalState;
    peoplePickerModal: PeoplePickerModalState;
    newChildModal: NewChildModalState;
    newEmployeeModal: NewEmployeeModalState;
    newContact: NewContactModalState;
    likers: any;
    video: any;
    recipients: any;
    addInvoice: any;
    addInvoiceV2: any;
    selectSession: any;
    invoice: any;
    editBillPayers: any;
    billPayerModal: any;
    linkContact: any;
    addPayment: any;
    deposit: any;
    event: any;
    product: any;
    session: any;
    organizationPersonManageRoles: OrganizationPersonManageRoleModal;
    postObservation: any;
    draftManager: any;
    choosePublicFunding: ChoosePublicFunding;
    credit: CreditModalState;
    accountItemMove: any;
    surcharge: SurchargeModalState;
    linkToFramework: any;
    moveRules: any;
    observation: any;
    help: any;
    reviewObservation: any;
    inquiries: any;
    purchase: any;
    qualification: QualificationModalState;
    paymentsVerification: PaymentsVerificationModalState;
    payNow: PayNowModalState;
    attendanceSchedule: AttendanceScheduleModalState;
    activityForm: ActivityFormModalState;
    activityLibrarySetting: ActivityLibrarySettingFormModalState;
    inviteParents: InviteParentsModalState;
    directDebitActivation: DirectDebitActivationState;
    reportAccidentIncident: ReportAccidentIncidentModalState;
    abstractBudget: AbstractBudgetModalState;
    payoutDetails: PayoutDetailsModalState;
    permissionForm: PermissionRoleModalState;
    organizationPersonProfile: OrganizationPersonProfileModalState;
    automaticPayments: AutomaticPaymentsModalState;
    vacationPlan: VacationPlanModalState;
    childPickup: ChildPickupModalState;
    newTag: NewTagModalState;
    addCard: AddCardModalState;
    switchRoomModal: SwitchRoomModalState;
    mealSettingsModal: MealSettingsModalState;
    addChildrenBulk: AddChildrenBulkModalState;
    signIn: SignInModalState;
    newMedicationForm: NewMedicationFormModalState;
    newMedicationFormAnyChild: NewMedicationFormModalAnyChildState;
    medicationForm: MedicationFormModalState;
    medicationAdministration: MedicationAdministrationModalState;
    activitiesModal: ActivitiesModalState;
    singleActivityModal: SingleActivityModalState;
    joinFamlyLiveMeetingModal: JoinFamlyLiveMeetingModalState;
    passwordModal: PromptPasswordModalState;
    registerTemperatureModal: RegisterTemperatureModalState;
    fileTaggingModal: FileTaggingModalState;
    assessmentModal: AssessmentModalState;
    assessmentHistoryModal: AssessmentHistoryModalState;
    reportUGCModal: ReportUGCModalState;
    observationEditor: ObservationEditorState;
    addPaymentMethodModal: AddPaymentMethodModalState;
    achInstantVerificationModal: AchInstantVerificationModalState;
    testChildActionsModal: TestChildActionsModalState;
    // lazy loaded
    applyCream?: ApplyCreamModalState;
    diaper?: any;
    organizationPerson: any;
    sleep?: any;
    voucherModal?: any;
}

export const modalContainerReducerMap: ReducersMapObject<any, Action<any>> = {
    modalsContainer: modalContainerReducer,
    holidayRSVP: holidayRSVPReducer,
    newConversation: newConversationReducer,
    advancedChildGroupMove: advancedChildGroupMoveReducer,
    childDevelopmentDrilldown: childDevelopmentDrilldownReducer,
    childProfileModal: childProfileModalReducer,
    peoplePickerModal: peoplePickerModalReducer,
    newChildModal: newChildModalReducer,
    newEmployeeModal: newEmployeeModalReducer,
    newContact: newContactModalReducer,
    likers: likersReducer,
    video: videoReducer,
    recipients: recipientsReducer,
    addInvoice: addInvoiceReducer,
    addInvoiceV2: addInvoiceV2Reducer,
    selectSession: selectSessionReducer,
    invoice: invoiceReducer,
    editBillPayers: editBillPayerReducer,
    billPayerModal: billPayerReducer,
    linkContact: linkContactReducer,
    addPayment: addPaymentReducer,
    deposit: depositModalReducer,
    event: eventModalReducer,
    product: productReducer,
    session: sessionReducer,
    organizationPersonManageRoles: organizationPersonManageRolesReducer,
    postObservation: postObservationModalReducer,
    draftManager: draftManagerModalReducer,
    choosePublicFunding: choosePublicFundingReducer,
    credit: creditModalReducer,
    accountItemMove: accountItemMoveReducer,
    surcharge: surchargeModalReducer,
    linkToFramework: linkToFrameworkModalReducer,
    moveRules: moveRulesReducer,
    observation: observationReducer,
    help: helpModalReducer,
    reviewObservation: reviewObservationReducer,
    inquiries: inquiryModalsReducer,
    purchase,
    qualification,
    paymentsVerification,
    payNow,
    attendanceSchedule,
    activityForm,
    activityLibrarySetting,
    inviteParents,
    directDebitActivation,
    reportAccidentIncident,
    abstractBudget,
    payoutDetails,
    permissionForm,
    organizationPersonProfile,
    automaticPayments,
    vacationPlan,
    childPickup,
    newTag,
    addCard,
    switchRoomModal,
    mealSettingsModal,
    addChildrenBulk,
    signIn,
    newMedicationForm,
    newMedicationFormAnyChild,
    medicationForm,
    medicationAdministration,
    activitiesModal,
    singleActivityModal,
    joinFamlyLiveMeetingModal,
    passwordModal,
    registerTemperatureModal: registerTemperatureModalReducer,
    fileTaggingModal,
    assessmentModal,
    assessmentHistoryModal,
    reportUGCModal,
    observationEditor,
    addPaymentMethodModal,
    achInstantVerificationModal,
    testChildActionsModal,
};

export default combineReducers<ModalsState>(modalContainerReducerMap);
